<template>
	<app-page>
		<app-heading :title="isNew ? 'New Analysis Preset' : analysispreset.name" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
					<app-button type="submit" small color="primary" v-else>Save</app-button>
					<app-button type="cancel" small :to="{ name: 'market-analysispresets' }">
						Cancel
					</app-button>
				</app-section-actions>
				<div class="title">Basic informations</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-text
							label="Name"
							v-model="analysispreset.name"
							required
							:errors="errors.name"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-select
							label="City"
							:items="cities"
							v-model="analysispreset.market_city"
							required
							:errors="errors.market_city"
						/>
					</app-cell>
				</app-row>
				<div v-if="$ravello.can('studeo.get')">
					<app-divider class="ma-4" />
					<div class="title">Link from Studeo historical data</div>
					<app-row>
						<app-cell cols="12" md="3">
							<input-switch
								v-model="analysispreset.from_studeo"
								label="Active"
								:disabled="studeoLinked || studeoLinking"
							/>
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" md="3">
							<input-select
								v-model="studeoYears"
								label="Years to include from Studeo"
								multiple
								:items="years"
								:disabled="!analysispreset.from_studeo || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="3">
							<input-select
								v-model="analysispreset.bedrooms"
								label="Bedrooms (Leave it empty to include all)"
								placeholder="All bedrooms"
								multiple
								:items="bedrooms"
								:disabled="!analysispreset.from_studeo || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="3">
							<input-select
								v-model="analysispreset.enterprise"
								label="Enterprise"
								:items="enterprises"
								:disabled="!analysispreset.from_studeo || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="3">
							<input-select
								v-model="analysispreset.resort"
								label="Resort"
								:items="resorts"
								:disabled="!analysispreset.enterprise || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="4">
							<input-select
								v-model="analysispreset.studeo_areas"
								label="Areas (Leave it empty to include all)"
								multiple
								:items="areas"
								:disabled="!analysispreset.resort || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="4">
							<input-select
								v-model="analysispreset.studeo_locations"
								label="Locations (Leave it empty to include all)"
								multiple
								:items="locations"
								:disabled="!analysispreset.resort || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12" md="4">
							<input-select
								v-model="analysispreset.studeo_complexes"
								label="Complexes (Leave it empty to include all)"
								multiple
								:items="complexes"
								:disabled="!analysispreset.resort || studeoLinked || studeoLinking"
							/>
						</app-cell>
						<app-cell cols="12">
							<app-button
								v-if="!studeoLinked"
								tile
								outlined
								color="primary"
								:loading="studeoLinking"
								:disabled="!analysispreset.from_studeo || !analysispreset.resort"
								@click="linkToStudeo"
							>
								Link
							</app-button>
							<app-button
								v-else
								tile
								outlined
								color="error"
								@click="unlinkStudeo"
							>
								Unlink
							</app-button>
						</app-cell>
					</app-row>
				</div>
				<app-divider class="ma-4" />
				<app-row>
					<app-cell cols="12" md="9">
						<div class="title">ADR historical data</div>
					</app-cell>
					<app-cell cols="12" md="3" class="text-right">
						<input-file
							type="csv"
							button
							small
							label="Import (CSV)"
							@load="onImport($event, 'avg')"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" md="6">
						<input-select
							v-model="analysispreset.avg_historical_years"
							label="Years to include in the historical data"
							multiple
							:items="years"
							@change="updateYears('avg')"
						/>
					</app-cell>
				</app-row>
				<app-data-table
					:items="avg"
					:headers="headersWithADRYears"
					:total="avg.length"
					:dynamic="false"
				>
					<template v-slot:actions="{ item }">
						<app-button
							v-if="$ravello.can('marketmath.put') && item.month !== 'Annual'"
							color="secondary"
							icon="fa-pencil-alt"
							table
							@click="onDataUpdate('avg', item.month)"
						>
							Edit
						</app-button>
					</template>
				</app-data-table>
				<app-divider class="ma-4" />
				<app-row>
					<app-cell cols="12" md="9">
						<div class="title">Occupancy historical data</div>
					</app-cell>
					<app-cell cols="12" md="3" class="text-right">
						<input-file
							type="csv"
							button
							small
							label="Import (CSV)"
							@load="onImport($event, 'occupancy')"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" md="6">
						<input-select
							v-model="analysispreset.occupancy_historical_years"
							label="Years to include in the historical data"
							multiple
							:items="years"
							@change="updateYears('occupancy')"
						/>
					</app-cell>
				</app-row>
				<app-data-table
					:items="occupancy"
					:headers="headersWithOccupancyYears"
					:total="occupancy.length"
					:dynamic="false"
				>
					<template v-slot:actions="{ item }">
						<app-button
							v-if="$ravello.can('marketmath.put') && item.month !== 'Annual'"
							color="secondary"
							icon="fa-pencil-alt"
							table
							@click="onDataUpdate('occupancy', item.month)"
						>
							Edit
						</app-button>
					</template>
				</app-data-table>
				<app-divider class="ma-4" />
				<div class="title">Occupancy forecast</div>
				<app-data-table
					:items="forecast"
					:headers="forecastHeaders"
					:total="forecast.length"
					:dynamic="false"
				/>
			</app-section>
		</app-form>
		<app-dialog v-model="editDialog.show" max-width="320" :title="`Updating ${editDialog.month}`">
			<app-dialog-body>
				<app-row v-for="entry in editDialog.data" :key="entry.key">
					<app-cell>
						<input-text
							v-model="entry.value"
							type="number"
							:label="entry.year"
							:icon="editDialog.type === 'occupancy' ? 'fa-percent' : 'fa-dollar-sign'"
						/>
					</app-cell>
				</app-row>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					:loading="editDialog.loading"
					@click="onDataUpdated"
				>Update</app-button>
				<app-button @click="editDialog.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import moment from 'moment'

import { range } from '@/utils/collection'

export default {
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			if (this.id !== 'new') {
				this.analysispreset = await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('analysispresets')
					.to(this.id)
					.get()

				if (this.analysispreset.from_studeo) {
					this.studeoLinked = true
				}
			} else {
				const years = range(0, 3).map(i => this.years[i]).reverse()
				this.analysispreset.occupancy_historical_years = [...years]
				this.analysispreset.avg_historical_years = [...years]
			}
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}

		this.cities = await this.$ravello.graphql('cities')
			.parent(this.$ravello.graphql('market', this.marketId))
			.datasource()
			.sort('name')
			.query()

		this.enterprises = await this.$ravello.graphql('enterprises')
			.datasource()
			.sort('name')
			.query()

		this.updateAll()
		this.$store.dispatch('app/stopLoading')
	},
	data() {
		return {
			analysispreset: {
				name: '',
				market_city: null,
				avg_historical_years: [],
				occupancy_historical_years: [],
				avg: {},
				occupancy: {},
				from_studeo: false,
				enterprise: null,
				resort: null,
				bedrooms: [],
				studeo_areas: [],
				studeo_locations: [],
				studeo_complexes: [],
			},
			studeoYears: [],
			studeoLinked: false,
			studeoLinking: false,
			cities: [],
			enterprises: [],
			resorts: [],
			areas: [],
			locations: [],
			complexes: [],
			headers: [{
				text: 'Month',
				value: 'month',
				sortable: false,
			}],
			forecastHeaders: [{
				text: 'Month',
				value: 'month',
			}, {
				text: '★★',
				value: 'rate_2',
				cb: item => `${item.rate_2 && item.rate_2.toFixed(2)}% (${item.nights_2})`,
			}, {
				text: '★★½',
				value: 'rate_2half',
				cb: item => `${item.rate_2half && item.rate_2half.toFixed(2)}% (${item.nights_2half})`,
			}, {
				text: '★★★',
				value: 'rate_3',
				cb: item => `${item.rate_3 && item.rate_3.toFixed(2)}% (${item.nights_3})`,
			}, {
				text: '★★★½',
				value: 'rate_3half',
				cb: item => `${item.rate_3half && item.rate_3half.toFixed(2)}% (${item.nights_3half})`,
			}, {
				text: '★★★★',
				value: 'rate_4',
				cb: item => `${item.rate_4 && item.rate_4.toFixed(2)}% (${item.nights_4})`,
			}, {
				text: '★★★★½',
				value: 'rate_4half',
				cb: item => `${item.rate_4half && item.rate_4half.toFixed(2)}% (${item.nights_4half})`,
			}, {
				text: '★★★★★',
				value: 'rate_5',
				cb: item => `${item.rate_5 && item.rate_5.toFixed(2)}% (${item.nights_5})`,
			}],
			editDialog: {
				show: false,
				month: '',
				loading: false,
				type: '',
				data: [],
			},
			occupancy: [],
			avg: [],
			errors: {},
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
		id() {
			return this.$route.params.preset
		},
		isNew() {
			return this.id === 'new'
		},
		years() {
			const start = moment()
			return range(0, 10).map(i => start.clone().subtract(i, 'years').format('YYYY'))
		},
		bedrooms() {
			return range(0, 9).map((i) => {
				const value = i
				switch (value) {
				case 0:
					return {
						text: 'Studio',
						value,
					}
				case 1:
					return {
						text: '1 Bedroom',
						value,
					}
				default:
					return {
						text: `${value} Bedrooms`,
						value,
					}
				}
			})
		},
		headersWithOccupancyYears() {
			return [
				...this.headers,
				...this.analysispreset.occupancy_historical_years.map(year => ({
					text: year,
					value: year,
					sortable: false,
					cb: item => `${item[year] ? item[year].toFixed(2) : 0}%`,
				})), {
					text: 'Average',
					value: 'average',
					sortable: false,
					cb: item => `${item.average ? item.average.toFixed(2) : 0}%`,
				}, {
					text: 'Actions',
					value: 'actions',
					align: 'end',
					sortable: false,
				},
			]
		},
		headersWithADRYears() {
			return [
				...this.headers,
				...this.analysispreset.avg_historical_years.map(year => ({
					text: year,
					value: year,
					sortable: false,
					cb: item => `$${item[year] ? item[year].toFixed(0) : 0}`,
				})), {
					text: 'Average',
					value: 'average',
					sortable: false,
					cb: item => `$${item.average ? item.average.toFixed(0) : 0}`,
				}, {
					text: '% revenue',
					value: 'percent_revenue',
					sortable: false,
					cb: item => `${item.percent_revenue ? item.percent_revenue.toFixed(2) : 0}%`,
				}, {
					text: 'Actions',
					value: 'actions',
					align: 'end',
					sortable: false,
				},
			]
		},
		forecast() {
			const forecast = this.occupancy.filter(entry => entry.month !== 'Annual').map((entry) => {
				const then = moment().date(1).month(entry.month)
				if (moment().diff(then, 'month') > 0) {
					then.add(1, 'year')
				}

				const { length } = Object.keys(entry).filter(key => key !== 'month' && entry[key] > 0)
				const occupancyRate = length
					? Object.keys(entry).reduce((acc, field) => {
						if (field === 'month') { return acc }
						return acc + entry[field]
					}, 0) / length : 0

				return {
					month: `${entry.month} ${then.format('YYYY')}`,
					...(range(0, 7).reduce((acc, i) => {
						const stars = (5 - (0.5 * i)).toString().replace('.5', 'half')
						const mod = (i * 0.05) - 0.1
						const rate = occupancyRate * (1 - mod)
						return {
							...acc,
							[`rate_${stars}`]: rate,
							[`nights_${stars}`]: Math.round(then.daysInMonth() * (rate / 100)),
						}
					}, {})),
				}
			})

			const yearlyForecast = forecast.reduce((acc, entry) => {
				Object.keys(entry).forEach((key) => {
					if (key === 'month') { return }
					if (!acc[key]) {
						acc[key] = 0
					}
					acc[key] += entry[key]
				})
				return acc
			}, {})
			Object.keys(yearlyForecast).forEach((key) => {
				if (/^rate_/.test(key)) {
					yearlyForecast[key] /= 12
				}
			})
			yearlyForecast.month = 'Annual'

			forecast.push(yearlyForecast)
			return forecast
		},
	},
	watch: {
		'analysispreset.enterprise': {
			async handler() {
				if (!this.analysispreset.enterprise) { return }
				this.resorts = await this.$ravello.graphql('resorts')
					.parent(this.$ravello.graphql('enterprise', this.analysispreset.enterprise))
					.datasource()
					.sort('name')
					.query()
			},
		},
		'analysispreset.resort': {
			async handler() {
				if (!this.analysispreset.resort) { return }
				const { complexes, areas, locations } = await this.$ravello.graphql('resort', this.analysispreset.resort)
					.include(this.$ravello.graphql('complexes').datasource('name', 'studeo_id'))
					.include(this.$ravello.graphql('areas').datasource('name', 'studeo_id'))
					.include(this.$ravello.graphql('locations').datasource('name', 'studeo_id'))
					.query()

				this.areas = areas
				this.complexes = complexes
				this.locations = locations
			},
		},
		'analysispreset.from_studeo': {
			handler(newVal, oldVal) {
				if (newVal === false && oldVal === true) {
					this.analysispreset.resort = null
					this.analysispreset.enterprise = null
					this.analysispreset.bedrooms = []
					this.analysispreset.studeo_areas = []
					this.analysispreset.studeo_locations = []
					this.analysispreset.studeo_complexes = []
					this.resorts = []
					this.areas = []
					this.complexes = []
					this.locations = []
				}
			},
		},
	},
	methods: {
		async onSubmit() {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('analysispresets')

			const payload = {
				name: this.analysispreset.name,
				market_city: this.analysispreset.market_city,
				avg_historical_years: this.analysispreset.avg_historical_years,
				occupancy_historical_years: this.analysispreset.occupancy_historical_years,
				avg: this.analysispreset.avg,
				occupancy: this.analysispreset.occupancy,
				...(this.studeoLinked && this.analysispreset.from_studeo ? {
					from_studeo: this.analysispreset.from_studeo,
					enterprise: this.analysispreset.enterprise,
					resort: this.analysispreset.resort,
					bedrooms: this.analysispreset.bedrooms,
					studeo_areas: this.analysispreset.studeo_areas,
					studeo_complexes: this.analysispreset.studeo_complexes,
					studeo_locations: this.analysispreset.studeo_locations,
				} : {
					from_studeo: false,
					enterprise: null,
					resort: null,
					bedrooms: [],
				}),
			}

			try {
				if (this.isNew) {
					await request.create(payload)
				} else {
					await request
						.to(this.id)
						.update(payload)
				}
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `Analysis Preset successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'market-analysispresets' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},

		getYearMonth(year, month) {
			return `${year}-${(month + 1).toString().padStart(2, 0)}`
		},

		getSupportedHistoricalYears(type) {
			return this.analysispreset[`${type}_historical_years`]
		},

		getYearlyData(type, month) {
			return this.getSupportedHistoricalYears(type).reduce((acc, year) => ({
				...acc,
				[year]: this.analysispreset[type][this.getYearMonth(year, month)],
			}), {})
		},

		getYearlyAverage(yearlyData) {
			const { length } = Object.keys(yearlyData).filter(key => key !== 'month' && yearlyData[key] > 0)
			return length
				? Object.keys(yearlyData).reduce((acc, field) => {
					if (field === 'month') { return acc }
					return acc + yearlyData[field]
				}, 0) / length : 0
		},

		getAnnualCompilation(data, type) {
			return {
				month: 'Annual',
				...this.getSupportedHistoricalYears(type).reduce((acc, year) => {
					const total = data.filter(entry => !!entry[year]).length
					const value = total
						? data.reduce((acc2, entry) => acc2 + entry[year] || 0, 0) / total
						: 0
					return { ...acc, [year]: value }
				}, {}),
				average: data.reduce((acc, entry) => acc + entry.average, 0) / 12,
			}
		},

		syncOccupancyTable() {
			const occupancy = range(0, 12).map((i) => {
				const yearlyData = this.getYearlyData('occupancy', i)
				const average = this.getYearlyAverage(yearlyData)

				return {
					month: moment.months(i),
					...yearlyData,
					average,
				}
			})

			occupancy.push(this.getAnnualCompilation(occupancy, 'occupancy'))
			this.occupancy = occupancy
		},

		syncADRTable() {
			const entries = range(0, 12).map((i) => {
				const yearlyData = this.getYearlyData('avg', i)
				const average = this.getYearlyAverage(yearlyData)

				return {
					month: moment.months(i),
					...yearlyData,
					average,
				}
			})

			const total = entries.reduce((acc, entry) => acc + entry.average, 0)
			const avg = entries.map(entry => ({
				...entry,
				percent_revenue: (entry.average / total) * 100,
			}))

			avg.push({
				...this.getAnnualCompilation(avg, 'avg'),
				percent_revenue: 100,
			})

			this.avg = avg
		},

		syncTable(type) {
			switch (type) {
			case 'occupancy':
				this.syncOccupancyTable()
				break
			case 'avg':
				this.syncADRTable()
				break
			default:
			}
		},

		updateData(type) {
			const validYearMonthsMap = {}
			const data = { ...this.analysispreset[type] }
			this.analysispreset[`${type}_historical_years`].forEach((year) => {
				range(0, 12).forEach((i) => {
					const yearMonth = this.getYearMonth(year, i)
					if (!data[yearMonth]) {
						data[yearMonth] = 0
					}
					validYearMonthsMap[yearMonth] = true
				})
			})
			Object.keys(data).forEach((yearMonth) => {
				if (!validYearMonthsMap[yearMonth]) {
					delete data[yearMonth]
				}
			})

			this.analysispreset[type] = data

			this.syncTable(type)
		},

		updateAll() {
			this.updateData('occupancy')
			this.updateData('avg')
		},

		onDataUpdate(type, month) {
			const monthNumber = moment().month(month).month()
			this.editDialog.month = month
			this.editDialog.data = this.analysispreset[`${type}_historical_years`].map(year => ({
				key: this.getYearMonth(year, monthNumber),
				year,
				value: this.analysispreset[type][this.getYearMonth(year, monthNumber)],
			}))
			this.editDialog.type = type
			this.editDialog.show = true
		},
		onDataUpdated() {
			const { type } = this.editDialog
			this.editDialog.loading = true
			this.editDialog.data.forEach((entry) => {
				this.analysispreset[type][entry.key] = parseFloat(entry.value)
			})
			this.syncTable(type)

			this.editDialog.loading = false
			this.editDialog.show = false
		},

		injectHistoricalData(type, field, entries) {
			const data = { ...this.analysispreset[type] }
			entries.forEach((entry) => {
				if (typeof data[entry.date] !== 'undefined') {
					data[entry.date] = entry[field]
				}
			})

			this.analysispreset[type] = data
			this.syncTable(type)
		},

		async linkToStudeo() {
			this.studeoLinking = true
			this.analysispreset.avg_historical_years = [...this.studeoYears]
			this.analysispreset.occupancy_historical_years = [...this.studeoYears]
			this.updateYears('avg')
			this.updateYears('occupancy')

			try {
				const {
					bedrooms,
					enterprise,
					resort,
					studeo_areas: areas,
					studeo_locations: locations,
					studeo_complexes: complexes,
				} = this.analysispreset
				const entries = await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('studeohistoricaldata')
					.params({
						enterprise,
						resort,
						years: this.studeoYears,
						...(bedrooms.length ? { bedrooms } : {}),
						...(areas.length ? { areas } : {}),
						...(locations.length ? { locations } : {}),
						...(complexes.length ? { complexes } : {}),
					})
					.get()

				this.injectHistoricalData('avg', 'adr', entries)
				this.injectHistoricalData('occupancy', 'occupancy', entries)
				this.studeoLinked = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
			this.studeoLinking = false
		},

		unlinkStudeo() {
			this.analysispreset.from_studeo = false
			this.studeoLinked = false
		},

		async onImport(importData, type) {
			if (!importData.length) {
				this.$notifications.pushAlert('error', 'Empty CSV dataset')
				return
			}
			if (!importData[0].month) {
				this.$notifications.pushAlert('error', 'Missing required header "month"')
				return
			}

			const data = { ...this.analysispreset[type] }
			importData.forEach((row) => {
				const monthNumber = moment().month(row.month).month()
				Object.keys(row).forEach((field) => {
					if (field === 'month') { return }
					const key = this.getYearMonth(field, monthNumber)
					const value = parseFloat(row[field].replace(/[^0-9.]/g, '')) || 0

					if (typeof data[key] !== 'undefined') {
						data[key] = value
					}
				})
			})

			this.analysispreset[type] = data
			this.syncTable(type)
		},

		sortYears(type) {
			this.analysispreset[`${type}_historical_years`].sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
		},

		updateYears(type) {
			this.sortYears(type)
			this.updateData(type)
		},
	},
}
</script>
